import { Link, Outlet } from "react-router-dom";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { isStaging } from "@/lib/env";

const RAIL_WIDTH = 56;

export default function AdminLayout() {
  return (
    <div style={{ display: "flex", minHeight: "100vh", alignItems: "stretch" }}>
      <NavRail />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Outlet />
      </div>
    </div>
  );
}

function NavRail() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const initial = user?.name?.charAt(0)?.toUpperCase() ?? "?";
  const brandColor = theme === "light" ? "#000" : "#fff";

  return (
    <nav
      aria-label="Admin"
      style={{
        width: RAIL_WIDTH,
        minWidth: RAIL_WIDTH,
        background: "var(--surface)",
        borderRight: "1px solid var(--border)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "sticky",
        top: 0,
        alignSelf: "flex-start",
        height: "100vh",
        padding: "8px 0",
        gap: 4,
      }}
    >
      {/* Environment badge — visible only on staging so you don't confuse it with prod */}
      {isStaging() && (
        <div
          title="Staging environment"
          style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            background: "#f58231",
            color: "#fff",
            fontSize: "0.6rem",
            fontWeight: 800,
            letterSpacing: "0.18em",
            padding: "8px 2px",
            marginBottom: 6,
            borderRadius: 4,
          }}
        >
          STAGING
        </div>
      )}

      {/* Brand / dashboard */}
      <Link
        to="/admin"
        title="TapVocab admin · Dashboard"
        aria-label="TapVocab admin · Dashboard"
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 40, height: 40, borderRadius: 8,
          background: "var(--accent)", color: brandColor,
          textDecoration: "none", fontWeight: 800, fontSize: "1.15rem",
          marginBottom: 8, fontFamily: "inherit",
        }}
      >
        T
      </Link>

      <RailIcon to="/landing" label="Back to app" icon="←" active={false} />

      <span style={{ flex: 1 }} />

      {/* Footer cluster: avatar (identity, not interactive) + explicit Log out button */}
      <ThemeToggle />
      {user && (
        <>
          <div
            title={user.name}
            aria-label={`Signed in as ${user.name}`}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32, borderRadius: 999,
              background: "var(--surface-sunken, transparent)",
              color: "var(--text)", fontWeight: 700, fontSize: "0.85rem",
              overflow: "hidden", marginTop: 6,
            }}
          >
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                referrerPolicy="no-referrer"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              initial
            )}
          </div>
          <button
            type="button"
            onClick={logout}
            title="Log out"
            aria-label="Log out"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              flexDirection: "column", gap: 1,
              width: 40, marginTop: 2, padding: "6px 0",
              border: "none", background: "transparent",
              cursor: "pointer", color: "var(--text-muted)",
              fontSize: "0.62rem", fontWeight: 600, lineHeight: 1,
            }}
          >
            <span aria-hidden="true" style={{ fontSize: "1.05rem", lineHeight: 1 }}>↪</span>
            <span>Log out</span>
          </button>
        </>
      )}
    </nav>
  );
}

function RailIcon({
  to, label, icon, active,
}: {
  to: string; label: string; icon: string; active: boolean;
}) {
  return (
    <Link
      to={to}
      title={label}
      aria-label={label}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 40, height: 40, borderRadius: 8,
        textDecoration: "none",
        color: active ? "var(--text)" : "var(--text-muted)",
        background: active ? "var(--surface-sunken, rgba(0,0,0,0.06))" : "transparent",
        fontSize: "1.15rem",
      }}
    >
      <span aria-hidden="true">{icon}</span>
    </Link>
  );
}
